import type {
  DailyRevenue,
  ExpenseEntry,
  Ingredient,
  InventoryCount,
  InventoryCountLine,
  MenuItem,
  Recipe,
} from './types';
import { effectiveLocationId } from './types';
import { isMaterialCategory, revenueOf } from './finance';
import { inventoryForPeriod } from './inventory';
import { addMonths, inPeriod, monthOf, periodOfMonth, type Period } from './period';
import { recipeDependencies } from './recipeGraph';
import { round } from './units';
import {
  addDays,
  daysBetween,
  fechaLarga,
  lastDayOfMonth,
  nombreMes,
  rangoEnPalabras,
  unitsInRange,
  type DishSalesPeriod,
} from './ventasPorPlato';

/**
 * Recuento con FECHA, TIPO y LOCAL (fase 7, oct 2026; notas «FECHAS: CIERRE O
 * INTERMEDIO» y «DECIDIDO CON ALEX» del 10 oct).
 *
 * - CIERRE: uno por mes y local; solo se hace del día 29 al 2 del mes
 *   siguiente. Es el que usa la cuenta de resultados.
 * - INTERMEDIO: cualquier día del mes; no cierra nada. Dice lo que gastaste
 *   desde el recuento anterior.
 *
 * Un recuento viejo (solo mes, sin local) se lee como cierre del último día
 * de su mes, ya cerrado, del local principal (que en un negocio de un solo
 * local es todo el negocio).
 */

export type InventoryCountKind = 'cierre' | 'intermedio';
export type InventoryCountStatus = 'abierto' | 'cerrado';

export const countKind = (c: Pick<InventoryCount, 'kind'>): InventoryCountKind => c.kind ?? 'cierre';
export const countStatus = (c: Pick<InventoryCount, 'status'>): InventoryCountStatus => c.status ?? 'cerrado';
export const countDate = (c: Pick<InventoryCount, 'date' | 'month'>): string => c.date ?? lastDayOfMonth(c.month);

/**
 * Hasta qué día valen las existencias de un recuento. Un cierre de septiembre
 * contado el 1 de octubre por la mañana es lo que quedaba el 30: vale «a 30
 * de septiembre». Un intermedio vale el día que se contó.
 */
export const countAsOf = (c: Pick<InventoryCount, 'date' | 'month' | 'kind'>): string =>
  countKind(c) === 'cierre' ? lastDayOfMonth(c.month) : countDate(c);

/** Lo que identifica un recuento: local + cierre del mes, o local + intermedio de un día. */
export interface InventoryCountKey {
  month: string;
  locationId?: string;
  kind?: InventoryCountKind;
  date?: string;
}

export function sameInventoryCount(c: InventoryCount, key: InventoryCountKey): boolean {
  if (effectiveLocationId(c.locationId) !== effectiveLocationId(key.locationId)) return false;
  const kind = key.kind ?? 'cierre';
  if (countKind(c) !== kind) return false;
  return kind === 'cierre' ? c.month === key.month : countDate(c) === key.date;
}

/** Texto corto para pintar un recuento: «Cierre de octubre», «Intermedio · 15 de octubre». */
export function countLabel(c: Pick<InventoryCount, 'date' | 'month' | 'kind'> & { atrasado?: InventoryCount['atrasado'] }): string {
  return countKind(c) === 'cierre'
    ? `Cierre ${c.atrasado ? 'atrasado ' : ''}de ${nombreMes(c.month)}`
    : `Intermedio · ${fechaLarga(countDate(c))}`;
}

/* ------------------------------------------------------------------ */
/* Ventana del cierre                                                  */
/* ------------------------------------------------------------------ */

/**
 * Ventana del cierre de un mes: del 29 (o el último día, en febrero) al 2 del
 * mes siguiente. Cubre a quien cuenta el 31 por la noche y a quien cuenta el
 * 1 por la mañana antes de recibir pedidos.
 */
export function cierreWindow(month: string): Period {
  const last = lastDayOfMonth(month);
  const start = Number(last.slice(8, 10)) >= 29 ? `${month}-29` : last;
  return { start, end: `${addMonths(month, 1)}-02` };
}

export interface CierreAvailability {
  /** `pronto`: todavía no se puede; `abierta`: hoy toca; `pasada`: se puede registrar con una fecha de la ventana. */
  state: 'pronto' | 'abierta' | 'pasada';
  window: Period;
  /** Días que faltan para que se abra (solo `pronto`). */
  daysLeft: number;
  /** Fecha que se propone al empezar. */
  defaultDate: string;
}

export function cierreAvailability(month: string, today: string): CierreAvailability {
  const window = cierreWindow(month);
  if (today < window.start) {
    return { state: 'pronto', window, daysLeft: daysBetween(today, window.start) - 1, defaultDate: lastDayOfMonth(month) };
  }
  if (today <= window.end) {
    const last = lastDayOfMonth(month);
    return { state: 'abierta', window, daysLeft: 0, defaultDate: today <= last ? today : last };
  }
  return { state: 'pasada', window, daysLeft: 0, defaultDate: lastDayOfMonth(month) };
}

/* ------------------------------------------------------------------ */
/* Cierre atrasado (revisión de Alex del 14 oct 2026)                  */
/* ------------------------------------------------------------------ */

/**
 * La ventana del cierre ya pasó y no hay cierre: se puede hacer un CIERRE
 * ATRASADO. Se cuenta lo que queda HOY y Alexia resta lo que se compró desde
 * el día 1 del mes siguiente para estimar lo que quedaba el último día.
 */
export function cierreAtrasadoPosible(month: string, today: string): boolean {
  return cierreAvailability(month, today).state === 'pasada';
}

/** Desde qué día se restan las compras: el 1 del mes siguiente, hasta el día contado. */
export function periodoCierreAtrasado(month: string, contadoEl: string): Period {
  return { start: `${addMonths(month, 1)}-01`, end: contadoEl };
}

/** Lo que quedaba el último día, estimado: lo contado menos lo comprado después (nunca menos de 0). */
export function estimarCierreAtrasado(valorContado: number, compradoDesde: number): number {
  return round(Math.max(0, valorContado - compradoDesde), 2);
}

/** El aviso del cierre atrasado, en las palabras de Alex. */
export function avisoCierreAtrasado(month: string): string {
  const siguiente = nombreMes(addMonths(month, 1));
  return `Cuenta lo que te queda HOY. Alexia restará lo que compraste desde el 1 de ${siguiente} para estimar lo que quedaba el ${Number(lastDayOfMonth(month).slice(8, 10))} de ${nombreMes(month)}. Es una estimación.`;
}

/** Compras de materia prima de un periodo (lo que «compraste» en la fórmula del recuento). */
export function comprasDeMateriaPrima(expenses: ExpenseEntry[], period: Period): number {
  return purchasesIn(expenses, period);
}

/**
 * Meses sin cierre de cada local, desde su primer recuento hasta el mes
 * anterior a `mesActual`. En el historial salen en ámbar, «Sin recuento»
 * (revisión del 14 oct 2026).
 */
export function mesesSinCierre(counts: InventoryCount[], mesActual: string): Array<{ month: string; loc: string }> {
  const porLocal = new Map<string, InventoryCount[]>();
  for (const c of counts) {
    const id = effectiveLocationId(c.locationId);
    porLocal.set(id, [...(porLocal.get(id) ?? []), c]);
  }
  const out: Array<{ month: string; loc: string }> = [];
  for (const [loc, delLocal] of porLocal) {
    const primero = delLocal.map((c) => c.month).sort()[0];
    if (!primero) continue;
    const conCierre = new Set(delLocal.filter((c) => countKind(c) === 'cierre').map((c) => c.month));
    for (let m = primero; m < mesActual; m = addMonths(m, 1)) {
      if (!conCierre.has(m)) out.push({ month: m, loc });
    }
  }
  return out;
}

/** Fecha que se propone para un intermedio de ese mes; null si el mes no ha empezado. */
export function defaultIntermedioDate(month: string, today: string): string | null {
  if (`${month}-01` > today) return null;
  return monthOf(today) === month ? today : lastDayOfMonth(month);
}

/**
 * Valida la fecha de un recuento. Devuelve el motivo en palabras, o null.
 * Es la regla de la API y de la demo; la pantalla la usa para bloquear antes.
 */
export function checkCountDate(input: { kind: InventoryCountKind; month: string; date: string; today: string; atrasado?: boolean }): string | null {
  const { kind, month, date, today } = input;
  if (date > today) return 'La fecha del recuento no puede ser de un día que todavía no llega.';
  if (kind === 'cierre' && input.atrasado) {
    // Cierre atrasado: solo cuando la ventana ya pasó, y con el día en que se cuenta.
    const w = cierreWindow(month);
    if (today <= w.end) return `El cierre de ${nombreMes(month)} todavía se puede hacer a tiempo: ${rangoEnPalabras(w.start, w.end)}.`;
    if (date <= w.end) return `Un cierre atrasado se cuenta después del ${Number(w.end.slice(8, 10))} de ${nombreMes(monthOf(w.end))}: lo que te queda el día que cuentas.`;
    return null;
  }
  if (kind === 'cierre') {
    const w = cierreWindow(month);
    if (date < w.start || date > w.end) {
      return `El cierre de ${nombreMes(month)} se hace ${rangoEnPalabras(w.start, w.end)}. Si cuentas antes, todo lo que compres y gastes después se quedaría fuera. Haz un recuento intermedio.`;
    }
    return null;
  }
  if (monthOf(date) !== month) return `Un recuento intermedio de ${nombreMes(month)} tiene que tener una fecha de ${nombreMes(month)}.`;
  return null;
}

/* ------------------------------------------------------------------ */
/* Guardado                                                            */
/* ------------------------------------------------------------------ */

export interface InventoryCountWrite {
  month: string;
  locationId?: string;
  kind?: InventoryCountKind;
  date?: string;
  status?: InventoryCountStatus;
  lines: InventoryCountLine[];
  totalValue: number;
  /** Cierre atrasado: `totalValue` ya es la estimación. */
  atrasado?: InventoryCount['atrasado'];
}

/**
 * Lo que queda guardado al escribir un recuento sobre el que ya había.
 *
 * - Sin `kind` ni `date` (clientes de antes) se guarda como siempre: cierre,
 *   con la fecha que ya tuviera.
 * - Sin `status` se conserva el que había (un recuento viejo sigue cerrado).
 * - Al cerrar se apunta cuándo; al reabrir se quita.
 */
export function mergeInventoryCount(
  existing: InventoryCount | null,
  write: InventoryCountWrite,
  tenantId: string,
  now: string,
): InventoryCount {
  const kind = write.kind ?? (existing ? countKind(existing) : 'cierre');
  const date = write.date ?? existing?.date;
  const status = write.status ?? existing?.status;
  const closedAt =
    status === 'cerrado' ? (existing?.status === 'cerrado' && existing.closedAt ? existing.closedAt : now) : undefined;
  const location = effectiveLocationId(write.locationId);
  return {
    tenantId,
    month: write.month,
    ...(location !== 'principal' ? { locationId: location } : {}),
    lines: write.lines,
    totalValue: write.totalValue,
    updatedAt: now,
    ...(write.kind || existing?.kind ? { kind } : {}),
    ...(date ? { date } : {}),
    ...(status ? { status } : {}),
    ...(closedAt ? { closedAt } : {}),
    ...(write.atrasado ? { atrasado: write.atrasado } : {}),
  };
}

/** Menos de la mitad de lo que toca contar: el resultado no es fiable (umbral aprobado). */
export function isIncompleteCount(counted: number, toCount: number): boolean {
  return toCount > 0 && counted < toCount / 2;
}

/* ------------------------------------------------------------------ */
/* Qué ingredientes salen en cada local                                */
/* ------------------------------------------------------------------ */

/**
 * Ingredientes que se cuentan en un local (decidido con Alex, 10 oct): sin
 * preguntar nada al dar de alta, Alexia lo deduce. Un ingrediente «es de» un
 * local si en los últimos 3 meses:
 * - se compró a su proveedor para ese local,
 * - va en un plato que se vendió allí, o
 * - ya se contó allí.
 * Lo marcado «No se usa aquí» sale. Si no hay nada de donde deducir, salen
 * todos (mejor de más que una lista vacía).
 */
export function ingredientsForLocation(input: {
  locationId: string;
  ingredients: Ingredient[];
  recipes: Recipe[];
  menuItems: MenuItem[];
  expenses: ExpenseEntry[];
  counts: InventoryCount[];
  dishSales: DishSalesPeriod[];
  today: string;
  notUsed?: string[];
}): { ingredientIds: string[]; deduced: boolean } {
  const loc = effectiveLocationId(input.locationId);
  const since = addDays(input.today, -92);
  const countable = input.ingredients.filter((i) => i.active && Boolean(i.purchaseUnit));
  const notUsed = new Set(input.notUsed ?? []);
  const found = new Set<string>();

  const suppliers = new Set(
    input.expenses
      .filter((e) => effectiveLocationId(e.locationId) === loc && e.date >= since && e.supplierId)
      .map((e) => e.supplierId as string),
  );
  for (const ing of countable) if (ing.supplierId && suppliers.has(ing.supplierId)) found.add(ing.ingredientId);

  const soldMenuItems = new Set<string>();
  for (const item of input.menuItems) {
    const units = item.unitsSoldByLocation ? item.unitsSoldByLocation[loc] ?? 0 : loc === 'principal' ? item.unitsSold ?? 0 : 0;
    if (units > 0) soldMenuItems.add(item.menuItemId);
  }
  for (const p of input.dishSales) {
    if (effectiveLocationId(p.locationId) !== loc || p.end < since) continue;
    for (const l of p.lines) if (l.units > 0) soldMenuItems.add(l.menuItemId);
  }
  const recipesById = new Map(input.recipes.map((r) => [r.recipeId, r]));
  for (const item of input.menuItems) {
    if (!soldMenuItems.has(item.menuItemId)) continue;
    const tree = [recipesById.get(item.recipeId), ...recipeDependencies(item.recipeId, input.recipes)];
    for (const r of tree) {
      for (const c of r?.components ?? []) if (c.ref.kind === 'ingredient') found.add(c.ref.ingredientId);
    }
  }

  for (const c of input.counts) {
    if (effectiveLocationId(c.locationId) !== loc || countAsOf(c) < since) continue;
    for (const l of c.lines) found.add(l.ingredientId);
  }

  const deduced = countable.filter((i) => found.has(i.ingredientId) && !notUsed.has(i.ingredientId));
  if (deduced.length === 0) {
    return { ingredientIds: countable.filter((i) => !notUsed.has(i.ingredientId)).map((i) => i.ingredientId), deduced: false };
  }
  return { ingredientIds: deduced.map((i) => i.ingredientId), deduced: true };
}

/* ------------------------------------------------------------------ */
/* Resultado: gasto real frente a gasto teórico                         */
/* ------------------------------------------------------------------ */

/** Un plato costeado: lo que cuesta hacerlo y lo que se cobra sin IVA. */
export interface InventoryResultDish {
  menuItemId: string;
  name: string;
  costPerPortion: number;
  sellingPriceNet: number;
}

export type TheoreticalSpend =
  | {
      status: 'ok';
      /** Σ unidades vendidas × costo de receta del periodo. */
      spend: number;
      /** Ausente si quien pregunta no ve ventas (`ocultarVentasDelRecuento`). */
      pct?: number;
      /** Real − teórico, en dinero y en puntos de % de ventas. */
      diff: number;
      diffPct?: number;
      /** % de las ventas del periodo que cubren las ventas por plato (ausente si no ve ventas). */
      coveragePct?: number;
      units: number;
    }
  | {
      status: 'sin-ventas' | 'sin-unidades' | 'pocas-unidades';
      coveragePct?: number;
      message: string;
    };

export interface InventoryResult {
  kind: InventoryCountKind;
  month: string;
  /** null = «Todos los locales». */
  locationId: string | null;
  period: Period;
  /** Fecha del recuento (o del último de los locales, en «Todos»). */
  date: string;
  status: InventoryCountStatus | 'sin-recuento';
  opening: number;
  hasOpening: boolean;
  /** Recuento del que salen las existencias iniciales, si hay. */
  openingFrom?: { kind: InventoryCountKind; date: string };
  purchases: number;
  closing: number;
  /** Lo que gastaste de verdad: iniciales + compras − finales. */
  realSpend: number;
  /** Ausente si quien pregunta no ve ventas (`ventasOcultas`). */
  sales?: number;
  realPct: number | null;
  /** % del mes anterior (solo cierres), para comparar. */
  previousMonthPct: number | null;
  counted: number;
  toCount: number;
  incomplete: boolean;
  theoretical: TheoreticalSpend;
  /** En «Todos»: locales que todavía no hicieron el cierre del mes. */
  missingLocations: string[];
  /** Quien pregunta no ve ventas: sin `sales` ni porcentajes, solo importes. */
  ventasOcultas?: true;
}

/**
 * Para quien no ve Ingresos (cocina, roles sin «Ingresos»): el resultado del
 * recuento sin nada que deje deducir las ventas — ni las ventas, ni los %
 * (real, del mes anterior, teórico y su diferencia). Quedan los importes de
 * materia prima.
 */
export function ocultarVentasDelRecuento(r: InventoryResult): InventoryResult {
  const { sales: _sales, ...rest } = r;
  let theoretical: TheoreticalSpend;
  if (r.theoretical.status === 'ok') {
    // Con la cobertura y las unidades por plato se podrían despejar las ventas.
    const { pct: _pct, diffPct: _diffPct, coveragePct: _cov, ...t } = r.theoretical;
    theoretical = t;
  } else {
    const { coveragePct: _cov, ...t } = r.theoretical;
    theoretical = {
      ...t,
      message: t.message.replace(
        /Tus ventas por plato cubren el \d+ % de lo que vendiste[^.]*\. Para comparar hace falta al menos el 80 %\./,
        'Tus ventas por plato no cubren lo bastante de lo que vendiste para comparar.',
      ),
    };
  }
  return { ...rest, realPct: null, previousMonthPct: null, theoretical, ventasOcultas: true };
}

export interface InventoryResultInput {
  target: { month: string; kind?: InventoryCountKind; date?: string; locationId?: string | null };
  /** Recuentos de TODO el negocio; aquí se filtran por local. */
  counts: InventoryCount[];
  /** Gastos y ventas YA en el ámbito pedido (el local, con su parte de los comunes, o todo). */
  expenses: ExpenseEntry[];
  revenues: DailyRevenue[];
  /** Cargas de ventas por plato de todo el negocio. */
  dishSales: DishSalesPeriod[];
  /** Platos costeados. Los que no tienen costo terminado van en `uncosted`. */
  dishes: InventoryResultDish[];
  uncosted?: string[];
  /** Cuántos ingredientes toca contar en cada local (para «Incompleto»). */
  toCountByLocation: Record<string, number>;
  /** Locales activos (para «Local 2 todavía no hizo el recuento»). */
  activeLocationIds: string[];
}

/** Mínimo de ventas que tienen que cubrir las unidades por plato (decidido con Alex). */
export const THEORETICAL_MIN_COVERAGE = 0.8;

const pctOf = (part: number, whole: number): number | null => (whole > 0 ? round((part / whole) * 100, 1) : null);

function purchasesIn(expenses: ExpenseEntry[], period: Period): number {
  return round(
    expenses
      .filter((e) => inPeriod(e.date, period) && isMaterialCategory(e.category))
      .reduce((a, e) => a + e.netAmount, 0),
    2,
  );
}

function salesIn(revenues: DailyRevenue[], period: Period): number {
  return round(revenues.filter((d) => inPeriod(d.date, period)).reduce((a, d) => a + revenueOf(d), 0), 2);
}

/**
 * Lo que dicen tus recetas que deberías haber gastado: unidades vendidas de
 * cada plato × su costo. Solo se compara si esas unidades suman al menos el
 * 80 % de lo que vendiste: un teórico con 5 platos de 40 sale absurdamente
 * bajo y parece un robo.
 */
export function theoreticalSpend(input: {
  period: Period;
  /** «en octubre» / «del 1 al 15 de octubre». */
  periodLabel: string;
  /** «de octubre» para «Carga las unidades vendidas de octubre»; sin él, `periodLabel`. */
  loadLabel?: string;
  sales: number;
  realSpend: number;
  dishSales: DishSalesPeriod[];
  dishes: InventoryResultDish[];
  uncosted?: string[];
  /** Local; ausente = todo el negocio. */
  locationId?: string;
}): TheoreticalSpend {
  const { period, periodLabel, sales } = input;
  if (!(sales > 0)) {
    return {
      status: 'sin-ventas',
      coveragePct: 0,
      message: `No hay ventas anotadas ${periodLabel}. Anótalas y te digo qué parte de lo que vendiste se fue en materia prima.`,
    };
  }
  const { units } = unitsInRange(input.dishSales, period, input.locationId);
  const byId = new Map(input.dishes.map((d) => [d.menuItemId, d]));
  let spend = 0;
  let covered = 0;
  let totalUnits = 0;
  for (const [menuItemId, u] of units) {
    const dish = byId.get(menuItemId);
    if (!dish || !(u > 0)) continue;
    spend += u * dish.costPerPortion;
    covered += u * dish.sellingPriceNet;
    totalUnits += u;
  }
  const coveragePct = round((covered / sales) * 100, 1);
  const cargar = `Carga las unidades vendidas ${input.loadLabel ?? periodLabel} y te digo cuánto deberías haber gastado según tus recetas.`;
  if (totalUnits === 0) return { status: 'sin-unidades', coveragePct: 0, message: cargar };
  if (covered / sales < THEORETICAL_MIN_COVERAGE) {
    const sinCosto = input.uncosted?.length
      ? ` ${input.uncosted.length === 1 ? `«${input.uncosted[0]}» no tiene` : `${input.uncosted.length} platos no tienen`} el costo terminado y no cuenta${input.uncosted.length === 1 ? '' : 'n'}.`
      : '';
    return {
      status: 'pocas-unidades',
      coveragePct,
      message: `Tus ventas por plato cubren el ${Math.round(coveragePct)} % de lo que vendiste ${periodLabel}. Para comparar hace falta al menos el 80 %.${sinCosto} ${cargar}`,
    };
  }
  const pct = round((spend / sales) * 100, 1);
  const realPct = round((input.realSpend / sales) * 100, 1);
  return {
    status: 'ok',
    spend: round(spend, 2),
    pct,
    diff: round(input.realSpend - spend, 2),
    diffPct: round(realPct - pct, 1),
    coveragePct,
    units: Math.round(totalUnits),
  };
}

/**
 * Resultado de un recuento: lo que gastaste de verdad y lo que dicen tus
 * recetas. Es la tarjeta de «Octubre cerrado en Local 1».
 *
 * - Cierre: el mes entero, como la cuenta de resultados (iniciales = cierre
 *   del mes anterior).
 * - Intermedio: desde el recuento anterior del local (el que sea) hasta este.
 *   Las compras con fecha posterior al recuento no entran.
 * - «Todos los locales» (locationId null): suma de los cierres del mes.
 */
export function inventoryResult(input: InventoryResultInput): InventoryResult {
  const { target } = input;
  const kind = target.kind ?? 'cierre';
  const all = target.locationId === null;
  const loc = all ? undefined : effectiveLocationId(target.locationId ?? undefined);
  const scoped = all ? input.counts : input.counts.filter((c) => effectiveLocationId(c.locationId) === loc);
  const toCountOf = (id: string) => input.toCountByLocation[id] ?? 0;

  if (kind === 'intermedio' && !all) {
    const date = target.date ?? lastDayOfMonth(target.month);
    const count = scoped.find((c) => sameInventoryCount(c, { month: target.month, locationId: loc, kind, date }));
    const previous = scoped
      .filter((c) => countAsOf(c) < date && c !== count && countStatus(c) === 'cerrado')
      .sort((a, b) => countAsOf(b).localeCompare(countAsOf(a)))[0];
    const start = previous ? addDays(countAsOf(previous), 1) : `${target.month}-01`;
    const period = { start, end: date };
    const purchases = purchasesIn(input.expenses, period);
    const opening = previous?.totalValue ?? 0;
    const closing = count?.totalValue ?? 0;
    const realSpend = round(opening + purchases - closing, 2);
    const sales = salesIn(input.revenues, period);
    const counted = count?.lines.length ?? 0;
    return {
      kind,
      month: target.month,
      locationId: loc ?? null,
      period,
      date,
      status: count ? countStatus(count) : 'sin-recuento',
      opening,
      hasOpening: Boolean(previous),
      ...(previous ? { openingFrom: { kind: countKind(previous), date: countDate(previous) } } : {}),
      purchases,
      closing,
      realSpend,
      sales,
      realPct: pctOf(realSpend, sales),
      previousMonthPct: null,
      counted,
      toCount: toCountOf(loc!),
      incomplete: isIncompleteCount(counted, toCountOf(loc!)),
      theoretical: theoreticalSpend({
        period,
        periodLabel: rangoEnPalabras(period.start, period.end),
        sales,
        realSpend,
        dishSales: input.dishSales,
        dishes: input.dishes,
        uncosted: input.uncosted,
        locationId: loc,
      }),
      missingLocations: [],
    };
  }

  // Cierre: el mes, como la cuenta de resultados.
  const cierres = scoped.filter((c) => countKind(c) === 'cierre');
  const realOfMonth = (month: string) => {
    const period = periodOfMonth(month);
    const inv = inventoryForPeriod(cierres, period);
    const purchases = purchasesIn(input.expenses, period);
    const realSpend = round(inv.opening + purchases - inv.closing, 2);
    const sales = salesIn(input.revenues, period);
    return { period, inv, purchases, realSpend, sales };
  };
  const now = realOfMonth(target.month);
  const delMes = cierres.filter((c) => c.month === target.month);
  const anterior = addMonths(target.month, -1);
  // Solo un cierre CERRADO del mes anterior da existencias iniciales (lo mismo
  // que mira `inventoryValueAt`); uno a medias no cuenta.
  const hayAnterior = cierres.some((c) => c.month === anterior && countStatus(c) === 'cerrado');
  const prev = hayAnterior ? realOfMonth(anterior) : null;
  const counted = delMes.reduce((a, c) => a + c.lines.length, 0);
  const toCount = delMes.reduce((a, c) => a + toCountOf(effectiveLocationId(c.locationId)), 0) || (loc ? toCountOf(loc) : 0);
  const dates = delMes.map(countDate).sort();
  const status: InventoryResult['status'] =
    delMes.length === 0 ? 'sin-recuento' : delMes.every((c) => countStatus(c) === 'cerrado') ? 'cerrado' : 'abierto';
  const missingLocations = all
    ? input.activeLocationIds.filter((id) => !delMes.some((c) => effectiveLocationId(c.locationId) === id && countStatus(c) === 'cerrado'))
    : [];

  return {
    kind: 'cierre',
    month: target.month,
    locationId: loc ?? null,
    period: now.period,
    date: dates[dates.length - 1] ?? lastDayOfMonth(target.month),
    status,
    opening: round(now.inv.opening, 2),
    hasOpening: hayAnterior,
    ...(hayAnterior ? { openingFrom: { kind: 'cierre' as const, date: lastDayOfMonth(anterior) } } : {}),
    purchases: now.purchases,
    closing: round(now.inv.closing, 2),
    realSpend: now.realSpend,
    sales: now.sales,
    realPct: pctOf(now.realSpend, now.sales),
    previousMonthPct: prev ? pctOf(prev.realSpend, prev.sales) : null,
    counted,
    toCount,
    incomplete: isIncompleteCount(counted, toCount),
    theoretical: theoreticalSpend({
      period: now.period,
      periodLabel: `en ${nombreMes(target.month)}`,
      loadLabel: `de ${nombreMes(target.month)}`,
      sales: now.sales,
      realSpend: now.realSpend,
      dishSales: input.dishSales,
      dishes: input.dishes,
      uncosted: input.uncosted,
      locationId: loc,
    }),
    missingLocations,
  };
}
